"use client";

import { ReviewRecord, ReviewSectionRecord } from "@graphql/types/graphql";
import { IconType } from "@types";
import classNames from "classnames";
import { Icon } from "components/atoms/Icon/Icon";
import { Pretitle } from "components/atoms/Pretitle/Pretitle";
import { Review } from "components/atoms/Review/Review";
import { Container } from "components/organisms/Container/Container";
import { useTranslations } from "next-intl";
import { FC, useEffect, useState } from "react";
import { Swiper } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { A11y } from "swiper/modules";
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react";
import s from "./ReviewSection.module.scss";

const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1064;

interface Props {
    data: ReviewSectionRecord;
}

export const ReviewSection: FC<Props> = ({
    data: { title, reviews },
}) => {
    const t = useTranslations("common");

    const [swiper, setSwiper] = useState<Swiper | null>(null);
    const [begin, setBegin] = useState(true);
    const [end, setEnd] = useState(false);
    const [cardsPerView, setCardsPerView] = useState(1);

    useEffect(() => {
        // update cards per view based on screen width
        // render cards in swiper or normal flexbox based on cards per view
        const updateCardsPerView = () => {
            const width = window.innerWidth;
            if (width >= DESKTOP_BREAKPOINT) {
                setCardsPerView(3);
            } else if (width >= TABLET_BREAKPOINT) {
                setCardsPerView(2);
            } else {
                setCardsPerView(1);
            }
        };

        window.addEventListener("resize", updateCardsPerView);
        updateCardsPerView();
        return () => window.removeEventListener("resize", updateCardsPerView);
    }, []);

    return (
        <Container>
            <Pretitle title={title} />
            {reviews.length > cardsPerView ? (
                <div className={classNames(s.Swiper, end && s.End)} >
                    <SwiperComponent
                        spaceBetween={16}
                        slidesPerView="auto"
                        breakpoints={{
                            768: {
                                width: 768,
                                spaceBetween: 24,
                            },
                        }}
                        modules={[A11y]}
                        onSwiper={(swiperItem) => {
                            setSwiper(swiperItem);
                        }}
                        onSlideChange={(swiperItem) => {
                            setBegin(swiperItem.isBeginning);
                            setEnd(swiperItem.isEnd);
                        }}
                    >

                        {reviews.map((review: ReviewRecord) => (
                            <SwiperSlide className={s.Slide} key={review.id}>
                                <Review data={review} />
                            </SwiperSlide>
                        ))}
                    </SwiperComponent>
                    <div>
                        <button
                            type="button"
                            className={classNames(s.ReviewButtonPrevious, {
                                [s.IsDisabled]: begin,
                            })}
                            onClick={() => swiper?.slidePrev()}
                            aria-label={t("previous")}
                        >
                            <Icon name={IconType.CaretFilled} size="m" />
                        </button>
                        <button
                            type="button"
                            className={classNames(s.ReviewButtonNext, {
                                [s.IsDisabled]: end,
                            })}
                            onClick={() => swiper?.slideNext()}
                            aria-label={t("next")}
                        >
                            <Icon name={IconType.CaretFilled} size="m" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className={s.ReviewsWrapper}>
                    {reviews.map((review: ReviewRecord) => (
                        <Review data={review} />
                    ))}
                </div>
            )}
        </Container >
    )
}